import { deriveInternationalMobileDraft } from '@/lib/phone/resolveInternationalMobile';
import { CONTENT_TYPE_OPTIONS } from './hireClippersOptions';

export const HIRE_CLIPPERS_STEPS = [
  'demandType',
  'objective',
  'contentType',
  'platforms',
  'budget',
  'contact',
] as const;

export type HireClippersStep = (typeof HIRE_CLIPPERS_STEPS)[number];

export type SingleChoiceStep = 'demandType' | 'objective' | 'platforms' | 'budget';

export interface HireClippersDraft {
  readonly demandType: string;
  readonly objective: string;
  readonly contentType: readonly string[];
  readonly platforms: string;
  readonly budget: string;
  readonly name: string;
  readonly company: string;
  readonly email: string;
  /** Country name as listed in `COUNTRIES` (e.g. "Brasil"). */
  readonly phoneCountry: string;
  readonly phone: string;
}

export const EMPTY_HIRE_CLIPPERS_DRAFT: HireClippersDraft = Object.freeze({
  demandType: '',
  objective: '',
  contentType: [],
  platforms: '',
  budget: '',
  name: '',
  company: '',
  email: '',
  phoneCountry: deriveInternationalMobileDraft(null).country,
  phone: '',
});

export interface HireClippersProfile {
  readonly name?: string | null;
  readonly email?: string | null;
  readonly phone_number?: string | null;
}

/**
 * Pre-fills the contact step from the account. Only blank fields are filled,
 * so a late `myData` never overwrites what the user typed.
 */
export function applyProfileToDraft(
  draft: HireClippersDraft,
  profile: HireClippersProfile | null | undefined,
): HireClippersDraft {
  if (!profile) return draft;

  const name = draft.name.trim() ? draft.name : (profile.name ?? '').trim();
  const email = draft.email.trim() ? draft.email : (profile.email ?? '').trim();
  if (draft.phone.trim() || !profile.phone_number) {
    if (name === draft.name && email === draft.email) return draft;
    return { ...draft, name, email };
  }

  const phoneDraft = deriveInternationalMobileDraft(profile.phone_number);
  return {
    ...draft,
    name,
    email,
    phoneCountry: phoneDraft.country,
    phone: phoneDraft.phone,
  };
}

export function toggleContentType(
  draft: HireClippersDraft,
  value: string,
): HireClippersDraft {
  const selected = new Set(draft.contentType);
  if (selected.has(value)) selected.delete(value);
  else selected.add(value);
  return {
    ...draft,
    contentType: CONTENT_TYPE_OPTIONS.map((option) => option.value).filter(
      (optionValue) => selected.has(optionValue),
    ),
  };
}
